import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Check, 
  Zap, 
  Shield, 
  Globe, 
  Palette, 
  ShoppingCart, 
  FileText, 
  Users, 
  Search,
  ArrowRight,
  Settings,
  RefreshCw,
  Database,
  Lock
} from 'lucide-react';
import { Link } from 'react-router-dom';

export const WordPressPage = () => {
  const features = [
    {
      icon: Zap,
      title: 'One-Click Installation',
      description: 'Install WordPress instantly with Softaculous auto-installer. No technical knowledge required - just click and your site is ready.',
    },
    {
      icon: Settings,
      title: 'cPanel Management',
      description: 'Manage your WordPress site easily through cPanel. Access files, databases, emails, and more from one intuitive dashboard.',
    },
    {
      icon: RefreshCw,
      title: 'Auto Updates',
      description: 'Keep your WordPress installation secure with automatic updates for core, themes, and plugins through Softaculous.',
    },
    {
      icon: Database,
      title: 'Easy Backups',
      description: 'Create and restore backups with one click. Softaculous automatically backs up your entire WordPress installation.',
    },
    {
      icon: Shield,
      title: 'Free SSL Certificate',
      description: "Every WordPress site gets a free Let's Encrypt SSL certificate for secure HTTPS connections.",
    },
    {
      icon: Lock,
      title: 'Security Hardening',
      description: 'Built-in security features and optimized server configuration to keep your WordPress site safe.',
    },
  ];

  const softaculousApps = [
    { icon: FileText, name: 'WordPress', desc: 'World\'s most popular CMS' },
    { icon: ShoppingCart, name: 'WooCommerce', desc: 'E-commerce solution' },
    { icon: Users, name: 'BuddyPress', desc: 'Social networking' },
    { icon: Palette, name: 'Starter Themes', desc: '100+ free themes' },
    { icon: Search, name: 'Yoast SEO', desc: 'SEO optimization' },
    { icon: Globe, name: 'Multisite', desc: 'Network of sites' },
  ];

  const whatYouCanBuild = [
    'Personal Blogs',
    'Business Websites',
    'Online Stores (WooCommerce)',
    'Portfolio Sites',
    'News & Magazine Sites',
    'Membership Sites',
    'Forums & Communities',
    'Landing Pages',
    'Educational Platforms',
    'Non-profit Websites',
  ];

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="py-24 bg-foreground relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/20 mb-6">
            <Zap className="w-4 h-4 mr-2" />
            Powered by Softaculous & cPanel
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mb-6">
            WordPress Hosting
          </h1>
          <p className="text-xl text-white/70 max-w-3xl mx-auto mb-8">
            Launch your WordPress website in minutes with one-click installation, 
            managed through cPanel and Softaculous auto-installer
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="primary-light" asChild>
              <a href="https://961host.net/order/index.php/store/web-hosting" target="_blank" rel="noopener noreferrer">
                View Hosting Plans
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
            <Button size="lg" variant="outline-dark" asChild>
              <a href="#features">
                Learn More
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* One-Click Install Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-semibold text-foreground mb-4">
              One-Click WordPress Installation
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Get your WordPress site up and running in under 5 minutes with Softaculous
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Steps */}
            <div className="space-y-6">
              {[
                { step: '1', title: 'Login to cPanel', desc: 'Access your hosting control panel with your credentials' },
                { step: '2', title: 'Open Softaculous', desc: 'Find WordPress in the auto-installer application list' },
                { step: '3', title: 'Click Install', desc: 'Fill in your site details and click the install button' },
                { step: '4', title: 'Start Building', desc: 'Your WordPress site is ready - start creating content!' },
              ].map((item, idx) => (
                <div key={idx} className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-semibold shrink-0">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                    <p className="text-muted-foreground text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Image/Card */}
            <Card className="card-premium p-8 text-center">
              <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <Globe className="w-10 h-10 text-primary" />
              </div>
              <h3 className="text-2xl font-semibold text-foreground mb-4">
                400+ One-Click Apps
              </h3>
              <p className="text-muted-foreground mb-6">
                Softaculous offers WordPress plus 400+ other applications including 
                Joomla, Drupal, Magento, PrestaShop, and more.
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {['WordPress', 'WooCommerce', 'Joomla', 'Drupal', 'Magento'].map((app) => (
                  <Badge key={app} variant="secondary" className="px-3 py-1">
                    {app}
                  </Badge>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-secondary/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-semibold text-foreground mb-4">
              WordPress Features
            </h2>
            <p className="text-lg text-muted-foreground">
              Everything you need to run a successful WordPress website
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <Card key={idx} className="card-premium">
                  <CardContent className="p-6 space-y-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* What You Can Build */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-semibold text-foreground mb-4">
              What You Can Build
            </h2>
            <p className="text-lg text-muted-foreground">
              WordPress powers over 40% of the web - build anything you imagine
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 max-w-5xl mx-auto">
            {whatYouCanBuild.map((item, idx) => (
              <div 
                key={idx}
                className="flex items-center gap-2 px-4 py-3 rounded-lg border border-border bg-white hover:bg-secondary transition-colors"
              >
                <Check className="w-4 h-4 text-primary shrink-0" />
                <span className="text-sm font-medium text-foreground">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Softaculous Apps */}
      <section className="py-20 bg-secondary/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-semibold text-foreground mb-4">
              Powered by Softaculous
            </h2>
            <p className="text-lg text-muted-foreground">
              The world's leading auto-installer, included free with your hosting
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {softaculousApps.map((app, idx) => {
              const Icon = app.icon;
              return (
                <Card key={idx} className="card-premium">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">{app.name}</h3>
                      <p className="text-sm text-muted-foreground">{app.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-foreground text-center">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-semibold text-white mb-4">
            Ready to Launch Your WordPress Site?
          </h2>
          <p className="text-xl text-white/70 mb-8">
            All our shared hosting plans include WordPress with one-click installation
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="primary-light" asChild>
              <a href="https://961host.net/order/index.php/store/web-hosting" target="_blank" rel="noopener noreferrer">
                View Hosting Plans
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
            <Button size="lg" variant="outline-dark" asChild>
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
